/**
 * Port of packages/contracts/src/contracts/shareLink.ts — keep in lockstep.
 *
 * Everything below the type definitions is display logic for the shared statement page. It is
 * kept free of React and of `fetch` itself so the formatting rules and the status handling can
 * be tested directly, the way `releaseManifest.ts` splits from `ReleasePanel.tsx`.
 */

/**
 * 2: adds `test`, set by the server on a payload shared from a test organisation, which the page
 * stamps with a TEST watermark. Version 1 payloads already stored still render as before.
 */
export const SHARE_PAYLOAD_VERSION = 2;
export const SHARE_PAYLOAD_MIN_VERSION = 1;

export const SHARE_KINDS = ['TRIP_STATEMENT', 'TRIP_STATUS'] as const;
export type ShareKind = (typeof SHARE_KINDS)[number];

export const SHARE_KIND_LABELS: Record<ShareKind, string> = {
  TRIP_STATEMENT: 'Statement',
  TRIP_STATUS: 'Live status',
};
export type ShareAudience = 'PARTY' | 'VEHICLE_PROVIDER';
export type ShareColumnType = 'text' | 'date' | 'currency' | 'number';
export type ShareCellValue = string | number | null;

export type ShareColumn = {
  key: string;
  label: string;
  type: ShareColumnType;
  align?: 'left' | 'right';
};

export type ShareSubTable = {
  id: string;
  label: string;
  columns: ShareColumn[];
  rows: { id: string; cells: Record<string, ShareCellValue> }[];
};

export type ShareRow = {
  id: string;
  cells: Record<string, ShareCellValue>;
  children?: ShareSubTable[];
};

export type ShareDataset = {
  id: string;
  name: string;
  columns: ShareColumn[];
  rows: ShareRow[];
  totals?: Record<string, ShareCellValue>;
};

export type ShareSummaryTile = {
  label: string;
  value: number | string;
  type: ShareColumnType;
  tone?: 'neutral' | 'positive' | 'negative' | 'warning';
};

export type ShareLetterhead = {
  name: string;
  address?: string;
  gstNo?: string;
  phone?: string;
  email?: string;
  logo?: string;
};

/**
 * Where a `TRIP_STATUS` share's trip stands. Mirrors the trip status codes
 * (`domain/tripStatus`), flow order then CANCELLED.
 *
 * REPORTED and CANCELLED were added without raising `SHARE_PAYLOAD_VERSION`: a renderer that
 * predates them still shows the sender's `headline` and falls back to its default tone for a
 * stage it does not know, so it displays them; raising the version would instead make it refuse
 * every new share outright.
 */
export const SHARE_STATUS_STAGES = [
  'BOOKED',
  'LOADING',
  'IN_TRANSIT',
  'REPORTED',
  'UNLOADING',
  'DELIVERED',
  'COMPLETED',
  'CANCELLED',
] as const;
export type ShareStatusStage = (typeof SHARE_STATUS_STAGES)[number];

/**
 * The header block of a `TRIP_STATUS` share — what a customer reads before the tables. Like the
 * rest of the payload it is a snapshot taken when the link was published or last updated; the
 * sender re-publishes to move it. Vehicle number and lane only: no driver or contact details.
 */
export type ShareStatusHeader = {
  /** Human wording of the stage, e.g. "In transit". */
  headline: string;
  stage: ShareStatusStage;
  /** ISO 8601 — when this snapshot was taken ("last updated"). */
  updatedAt: string;
  vehicleNo?: string;
  from?: string;
  to?: string;
  /** `YYYY-MM-DD`. */
  dispatchDate?: string;
  /** `YYYY-MM-DD`. */
  expectedDelivery?: string;
  /** Last known position, as a place name. */
  lastLocation?: string;
  /** ISO 8601 — when `lastLocation` was recorded. */
  lastLocationAt?: string;
};

export type SharePayload = {
  version: number;
  /** Shared from a test organisation: the page carries a TEST watermark. */
  test?: true;
  kind: ShareKind;
  title: string;
  audience: ShareAudience;
  letterhead: ShareLetterhead;
  recipient?: { name: string };
  message?: string;
  currency: 'INR';
  generatedAt: string;
  period?: { from: string; to: string };
  /** Required when `kind` is `TRIP_STATUS`; absent otherwise. */
  status?: ShareStatusHeader;
  summary: ShareSummaryTile[];
  datasets: ShareDataset[];
};

export const SHARE_STATUS_STAGE_LABELS: Record<ShareStatusStage, string> = {
  BOOKED: 'Booked',
  LOADING: 'Loading',
  IN_TRANSIT: 'In transit',
  REPORTED: 'Reported at destination',
  UNLOADING: 'Unloading',
  DELIVERED: 'Delivered',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export type ShareMeta = {
  kind: ShareKind;
  title: string;
  audience: ShareAudience;
  expiresAt: string;
  createdAt: string;
};

/** What the page holds once a statement has actually been opened. */
export type ShareDocument = { meta: ShareMeta; payload: SharePayload };

// ------------------------------------------------------------------------------- formatting

const NBSP_DASH = '—';

export function formatCurrency(value: ShareCellValue | undefined, currency = 'INR'): string {
  if (value === null || value === undefined || value === '') return NBSP_DASH;
  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount)) return String(value);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatNumber(value: ShareCellValue | undefined): string {
  if (value === null || value === undefined || value === '') return NBSP_DASH;
  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount)) return String(value);
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(amount);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A calendar date is parsed by hand rather than through `new Date('2026-09-05')`, which is UTC
 * midnight and shows as the previous day for anyone west of Greenwich.
 */
export function formatDate(value: ShareCellValue | undefined): string {
  if (value === null || value === undefined || value === '') return NBSP_DASH;
  const text = String(value);
  const calendar = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (calendar) {
    const [, year, month, day] = calendar;
    const monthLabel = MONTHS[Number(month) - 1];
    if (!monthLabel) return text;
    return `${Number(day)} ${monthLabel} ${year}`;
  }
  const parsed = Date.parse(text);
  if (Number.isNaN(parsed)) return text;
  const date = new Date(parsed);
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** Timestamps are shown in the sender's timezone, since that is the clock the expiry was set on. */
export function formatDateTime(value: string | undefined | null): string {
  if (!value) return NBSP_DASH;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  }).format(new Date(parsed));
}

export function formatCell(column: ShareColumn, value: ShareCellValue | undefined): string {
  if (column.type === 'currency') return formatCurrency(value);
  if (column.type === 'number') return formatNumber(value);
  if (column.type === 'date') return formatDate(value);
  if (value === null || value === undefined || value === '') return NBSP_DASH;
  return String(value);
}

export function formatTile(tile: ShareSummaryTile): string {
  if (tile.type === 'currency') return formatCurrency(tile.value);
  if (tile.type === 'number') return formatNumber(tile.value);
  if (tile.type === 'date') return formatDate(tile.value);
  return String(tile.value);
}

export function columnAlign(column: ShareColumn): 'left' | 'right' {
  return (
    column.align ?? (column.type === 'currency' || column.type === 'number' ? 'right' : 'left')
  );
}

/** Server totals win; summing here is only a fallback so a footer is never silently wrong. */
export function computeTotals(dataset: ShareDataset): Record<string, ShareCellValue> | null {
  if (dataset.totals) return dataset.totals;

  const currencyColumns = dataset.columns.filter(column => column.type === 'currency');
  if (currencyColumns.length === 0) return null;

  const totals: Record<string, ShareCellValue> = {};
  currencyColumns.forEach(column => {
    totals[column.key] = dataset.rows.reduce((sum, row) => {
      const value = row.cells[column.key];
      return sum + (typeof value === 'number' ? value : 0);
    }, 0);
  });
  return totals;
}

export function isExpired(expiresAt: string | undefined | null, now = Date.now()): boolean {
  if (!expiresAt) return false;
  const parsed = Date.parse(expiresAt);
  return !Number.isNaN(parsed) && parsed <= now;
}

export function expiryNote(expiresAt: string | undefined | null): string {
  if (!expiresAt) return 'This link does not expire.';
  if (isExpired(expiresAt)) return `This link expired on ${formatDateTime(expiresAt)}.`;
  return `This link expires on ${formatDateTime(expiresAt)}.`;
}

export const AUDIENCE_LABELS: Record<ShareAudience, string> = {
  PARTY: 'Statement for party',
  VEHICLE_PROVIDER: 'Statement for vehicle provider',
};

export function audienceLabel(audience: ShareAudience): string {
  return AUDIENCE_LABELS[audience] ?? 'Statement';
}

/** The eyebrow over a document: a statement says who it is for, a live status says what it is. */
export function documentLabel(kind: ShareKind, audience: ShareAudience): string {
  if (kind === 'TRIP_STATUS') return 'Live trip status';
  return audienceLabel(audience);
}

export function isKnownKind(kind: unknown): kind is ShareKind {
  return typeof kind === 'string' && (SHARE_KINDS as readonly string[]).includes(kind);
}

/**
 * The pill's colour family; the page only distinguishes moving, arrived and closed. A cancelled
 * trip is closed too: it will not move again, and it is not an alarm for the customer.
 */
export type StageTone = 'active' | 'done' | 'closed';

export function stageTone(stage: ShareStatusStage): StageTone {
  if (stage === 'DELIVERED') return 'done';
  if (stage === 'COMPLETED' || stage === 'CANCELLED') return 'closed';
  return 'active';
}

export function stageLabel(header: Pick<ShareStatusHeader, 'headline' | 'stage'>): string {
  const headline = header.headline?.trim();
  return headline || SHARE_STATUS_STAGE_LABELS[header.stage] || String(header.stage);
}

/** "Jaipur → Mumbai", or whichever end is known; nothing when neither is. */
export function laneLabel(header: Pick<ShareStatusHeader, 'from' | 'to'>): string {
  const from = header.from?.trim();
  const to = header.to?.trim();
  if (from && to) return `${from} → ${to}`;
  return from || to || '';
}

// ------------------------------------------------------------------------------------- api

export type ShareResult =
  | { ok: 'ready'; document: ShareDocument }
  | { ok: 'password'; meta: Pick<ShareMeta, 'kind' | 'title'> }
  | { ok: 'wrong-password'; attemptsRemaining?: number }
  | { ok: 'locked'; lockedUntil?: string; retryAfterSeconds?: number }
  | { ok: 'not-found' }
  | { ok: 'rate-limited'; retryAfterSeconds?: number }
  | { ok: 'unsupported' }
  | { ok: 'error' };

export function shareApiBase(): string | undefined {
  const base = import.meta.env.VITE_SHARE_API_URL;
  if (!base) return undefined;
  return String(base).replace(/\/+$/, '');
}

type ResponseBody = Record<string, unknown> | null;

/**
 * The one place a status code becomes a page state. Kept pure so every branch is testable
 * without a network, and so an unexpected shape lands on "error" rather than a blank page.
 */
export function interpretResponse(status: number, body: ResponseBody): ShareResult {
  if (status === 200 && body) {
    if (body.requiresPassword === true) {
      const meta = body.meta as Pick<ShareMeta, 'kind' | 'title'> | undefined;
      return {
        ok: 'password',
        meta: meta ?? { kind: 'TRIP_STATEMENT', title: 'Shared statement' },
      };
    }
    const payload = body.payload as SharePayload | undefined;
    const meta = body.meta as ShareMeta | undefined;
    if (!payload || !meta) return { ok: 'error' };
    if (
      !Number.isInteger(payload.version) ||
      payload.version < SHARE_PAYLOAD_MIN_VERSION ||
      payload.version > SHARE_PAYLOAD_VERSION ||
      !isKnownKind(payload.kind)
    ) {
      return { ok: 'unsupported' };
    }
    // A live-status page is its header; without one there is nothing to put above the tables.
    if (payload.kind === 'TRIP_STATUS' && !payload.status?.stage) return { ok: 'error' };
    return { ok: 'ready', document: { meta, payload } };
  }

  if (status === 401) {
    if (body?.requiresPassword === true) {
      const meta = body.meta as Pick<ShareMeta, 'kind' | 'title'> | undefined;
      return {
        ok: 'password',
        meta: meta ?? { kind: 'TRIP_STATEMENT', title: 'Shared statement' },
      };
    }
    return {
      ok: 'wrong-password',
      ...(typeof body?.attemptsRemaining === 'number'
        ? { attemptsRemaining: body.attemptsRemaining }
        : {}),
    };
  }

  if (status === 423) {
    return {
      ok: 'locked',
      ...(typeof body?.lockedUntil === 'string' ? { lockedUntil: body.lockedUntil } : {}),
      ...(typeof body?.retryAfterSeconds === 'number'
        ? { retryAfterSeconds: body.retryAfterSeconds }
        : {}),
    };
  }

  if (status === 404) return { ok: 'not-found' };
  if (status === 429) {
    return {
      ok: 'rate-limited',
      ...(typeof body?.retryAfterSeconds === 'number'
        ? { retryAfterSeconds: body.retryAfterSeconds }
        : {}),
    };
  }

  return { ok: 'error' };
}

async function readBody(response: Response): Promise<ResponseBody> {
  try {
    return (await response.json()) as ResponseBody;
  } catch {
    return null;
  }
}

export async function fetchShare(
  base: string,
  token: string,
  signal?: AbortSignal,
): Promise<ShareResult> {
  try {
    const response = await fetch(`${base}/share/${encodeURIComponent(token)}`, {
      cache: 'no-store',
      signal,
    });
    return interpretResponse(response.status, await readBody(response));
  } catch {
    return { ok: 'error' };
  }
}

export async function unlockShare(
  base: string,
  token: string,
  password: string,
  signal?: AbortSignal,
): Promise<ShareResult> {
  try {
    const response = await fetch(`${base}/share/${encodeURIComponent(token)}/unlock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
      cache: 'no-store',
      signal,
    });
    return interpretResponse(response.status, await readBody(response));
  } catch {
    return { ok: 'error' };
  }
}

// --------------------------------------------------------------------- per-viewer conveniences

export const sessionKey = (token: string): string => `tb_share_pw:${token}`;

/**
 * Keeping the password for the tab means a refresh does not re-prompt. It never leaves the
 * browser, and every access is guarded because private modes throw rather than return null.
 */
export function readSavedPassword(token: string): string | null {
  try {
    return window.sessionStorage.getItem(sessionKey(token));
  } catch {
    return null;
  }
}

export function savePassword(token: string, password: string): void {
  try {
    window.sessionStorage.setItem(sessionKey(token), password);
  } catch {
    // A viewer who blocks storage simply re-enters the password.
  }
}

export function clearPassword(token: string): void {
  try {
    window.sessionStorage.removeItem(sessionKey(token));
  } catch {
    // As above.
  }
}
